import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, DollarSign, TrendingDown, TrendingUp } from "lucide-react";
import { toast } from "sonner";

export default function Finance() {
    const { activeHotelId } = useAuth();
    const [summary, setSummary] = useState({ revenue: 0, expenses: 0, profit: 0 });
    const [expenses, setExpenses] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ category: "utilities", amount: 0, description: "", payment_method: "cash" });

    const load = async () => {
        if (!activeHotelId) return;
        try {
            const [s, e] = await Promise.all([
                api.get(`/finance/summary?hotel_id=${activeHotelId}`),
                api.get(`/finance/expenses?hotel_id=${activeHotelId}`),
            ]);
            setSummary(s.data); setExpenses(e.data);
        } catch (er) { toast.error(formatApiError(er)); }
    };

    useEffect(() => { load(); }, [activeHotelId]);

    const submit = async () => {
        try {
            await api.post("/finance/expenses", { ...form, hotel_id: activeHotelId, amount: Number(form.amount) });
            toast.success("Expense recorded");
            setOpen(false); setForm({ category: "utilities", amount: 0, description: "", payment_method: "cash" }); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <AppShell title="Finance" subtitle="Revenue, expenses, ledger">
            <div className="grid grid-cols-3 gap-4 mb-6">
                <Card className="p-5 border border-slate-200 rounded-md shadow-none">
                    <div className="flex justify-between items-start">
                        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Revenue</div>
                        <TrendingUp className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div className="text-3xl font-display font-bold mt-2 text-slate-900">₹{Number(summary.revenue).toLocaleString()}</div>
                </Card>
                <Card className="p-5 border border-slate-200 rounded-md shadow-none">
                    <div className="flex justify-between items-start">
                        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Expenses</div>
                        <TrendingDown className="w-4 h-4 text-red-600" />
                    </div>
                    <div className="text-3xl font-display font-bold mt-2 text-slate-900">₹{Number(summary.expenses).toLocaleString()}</div>
                </Card>
                <Card className="p-5 border border-[#0A1C3E] rounded-md shadow-none bg-slate-50">
                    <div className="flex justify-between items-start">
                        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Net Profit</div>
                        <DollarSign className="w-4 h-4 text-[#C5B08A]" />
                    </div>
                    <div className={`text-3xl font-display font-bold mt-2 ${summary.profit >= 0 ? "text-emerald-700" : "text-red-700"}`}>₹{Number(summary.profit).toLocaleString()}</div>
                </Card>
            </div>

            <div className="flex justify-between items-center mb-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Expenses Ledger</div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild><Button className="bg-[#0A1C3E] rounded-sm" data-testid="exp-add-btn"><Plus className="w-4 h-4 mr-1" /> Expense</Button></DialogTrigger>
                    <DialogContent>
                        <DialogHeader><DialogTitle>Record Expense</DialogTitle></DialogHeader>
                        <div className="space-y-3">
                            <div><Label className="text-xs uppercase tracking-wider">Category</Label>
                                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="utilities">Utilities</SelectItem>
                                        <SelectItem value="salary">Salary</SelectItem>
                                        <SelectItem value="purchase">Purchase</SelectItem>
                                        <SelectItem value="maintenance">Maintenance</SelectItem>
                                        <SelectItem value="other">Other</SelectItem>
                                    </SelectContent>
                                </Select></div>
                            <div><Label className="text-xs uppercase tracking-wider">Amount</Label><Input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} data-testid="exp-amount" /></div>
                            <div><Label className="text-xs uppercase tracking-wider">Description</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="exp-desc" /></div>
                            <div><Label className="text-xs uppercase tracking-wider">Payment Method</Label>
                                <Select value={form.payment_method} onValueChange={v => setForm({ ...form, payment_method: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="cash">Cash</SelectItem>
                                        <SelectItem value="bank">Bank</SelectItem>
                                        <SelectItem value="upi">UPI</SelectItem>
                                        <SelectItem value="card">Card</SelectItem>
                                    </SelectContent>
                                </Select></div>
                        </div>
                        <DialogFooter><Button onClick={submit} className="bg-[#0A1C3E]" data-testid="exp-submit">Save</Button></DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            <Card className="border border-slate-200 rounded-md overflow-hidden shadow-none">
                <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider text-left">
                        <tr><th className="px-4 py-3">Date</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Description</th><th className="px-4 py-3">Method</th><th className="px-4 py-3 text-right">Amount</th></tr>
                    </thead>
                    <tbody>
                        {expenses.map(e => (
                            <tr key={e.id} className="border-b border-slate-100" data-testid={`exp-row-${e.id}`}>
                                <td className="px-4 py-3 text-xs text-slate-500">{e.date?.slice(0, 10)}</td>
                                <td className="px-4 py-3 text-xs uppercase tracking-wider text-slate-600">{e.category}</td>
                                <td className="px-4 py-3 text-sm">{e.description}</td>
                                <td className="px-4 py-3 text-xs uppercase text-slate-500">{e.payment_method}</td>
                                <td className="px-4 py-3 text-sm font-mono font-semibold text-right">₹{Number(e.amount).toLocaleString()}</td>
                            </tr>
                        ))}
                        {expenses.length === 0 && <tr><td colSpan="5" className="text-center py-8 text-sm text-slate-500">No expenses yet</td></tr>}
                    </tbody>
                </table>
            </Card>
        </AppShell>
    );
}
